import {
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { Broadcast, Status } from "entities/broadcasts/types";
import Chip from "shared/components/Chip";
import DropdownList from "shared/components/DropdownList";
import { DrawerHookResult } from "shared/hooks/useDrawerState";
import { chipTitleMap, chipColorMap } from "../../utils";

const TIMEZONE_MINUTES_REGEX = /([+-]\d{1,2})$/;
const TIMEZONE_FORMAT_REGEX = /([+-]\d{1,2}):(\d{2})$/;

const renderStatusCell = (params: GridRenderCellParams) => {
  const color = chipColorMap[params.value as Status];
  const title = chipTitleMap[params.value as Status];

  return <Chip title={title} bgColor={color} />;
};

const formatScheduledDateTime = (scheduledAt: string): string => {
  try {
    const [dateStr, timeStr, timezone] = scheduledAt.split(" ");
    const date = new Date(dateStr + "T" + timeStr);

    // Format the date in the desired format with day, month, year
    const formattedDate = format(date, "dd MMM yyyy, HH:mm");

    // Get timezone offset using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(date);
    const gmtOffset =
      parts.find((part) => part.type === "timeZoneName")?.value ?? "";

    // Replace GMT with UTC and ensure minutes are shown in offset
    const utcOffset = gmtOffset
      .replace("GMT", "UTC")
      .replace(TIMEZONE_MINUTES_REGEX, "$1:00")
      .replace(TIMEZONE_FORMAT_REGEX, "$1:$2");

    return `${formattedDate} ${utcOffset}`;
  } catch (e) {
    console.error("Error formatting scheduled date:", e);
    return scheduledAt;
  }
};

export const getUserColumns = (
  broadcastDetailsDrawer: DrawerHookResult<Broadcast>,
  onPublish?: (id: string, status: Status) => void,
) => {
  return [
    {
      field: "message",
      headerName: "Message",
      editable: false,
      sortable: true,
      flex: 6,
      renderCell: (item: GridRenderCellParams) => item.row.message.text,
    },
    {
      field: "createdAt",
      headerName: "Date Sent/Scheduled",
      editable: false,
      sortable: true,
      flex: 2,
      renderCell: (params: GridValueGetterParams) => {
        const status = params.row.status as Status;
        const isDraft = status === Status.Draft;

        // For completed sent broadcasts with scheduledAt, show the scheduled time with timezone
        if (!isDraft && params.row.scheduledAt) {
          return formatScheduledDateTime(params.row.scheduledAt);
        }

        // For regular broadcasts, show created time
        if (!isDraft && params.value) {
          return format(params.value, "dd MMM yyyy, HH:mm");
        }

        return "-";
      },
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: renderStatusCell,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      editable: false,
      align: "right",
      headerAlign: "right",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row.status as Status;
        const isCompleted = params.row.isCompleted;
        const items = [];

        switch (status) {
          case Status.Sent:
            items.push({
              title: "View",
              onClick: () => broadcastDetailsDrawer.openDrawer(params.row.id),
            });
            break;
          case Status.Scheduled:
            items.push({
              title: isCompleted ? "View" : "Edit",
              onClick: () => broadcastDetailsDrawer.openDrawer(params.row.id),
            });
            break;
          case Status.Draft:
            items.push(
              {
                title: "Edit",
                onClick: () => broadcastDetailsDrawer.openDrawer(params.row.id),
              },
              {
                title: "Publish",
                onClick: () => onPublish?.(params.row.id, Status.Sent),
              },
            );
            break;
        }

        return <DropdownList items={items} />;
      },
    },
  ];
};
